// src/components/notifications/BroadcastMessageDialog.tsx
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { Campaign as CampaignIcon } from '@mui/icons-material';
import DialogBase from '../common/modal/DialogBase';
import {
  DailogLabel,
  DialogLabelText,
  StyledTextField,
} from '../forms/shared/FormElements';
import DatePicker from '../forms/shared/DatePicker';
import { ModalCancelButton, ModalSaveButton } from '../common/Buttons';

export interface BroadcastMessageData {
  title: string;
  body: string;
  expiresAt?: string;
}

interface BroadcastMessageDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: BroadcastMessageData) => void;
  sending: boolean;
}

// No category field, deliberately: every broadcast composed here is filed
// as "System" server-side. Letting an admin pick a category would risk a
// human-authored announcement landing under an automated, opt-out-able
// one (e.g. "Backup") - see api.ts's notificationsAPI.broadcast().
const BroadcastMessageDialog: React.FC<BroadcastMessageDialogProps> = ({
  open,
  onClose,
  onConfirm,
  sending,
}) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  useEffect(() => {
    if (open) {
      setTitle('');
      setBody('');
      setExpiresAt('');
    }
  }, [open]);

  const canSave = title.trim().length > 0 && body.trim().length > 0 && !sending;

  const handleSave = () => {
    if (!canSave) return;
    onConfirm({
      title: title.trim(),
      body: body.trim(),
      expiresAt: expiresAt || undefined,
    });
  };

  return (
    <DialogBase
      open={open}
      onClose={onClose}
      title="Broadcast Message"
      headerIcon={<CampaignIcon />}
      maxWidth="sm"
      draggable
      footer={
        <>
          <ModalCancelButton
            onClick={onClose}
            disabled={sending}
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
            }}
          />
          <ModalSaveButton
            onClick={handleSave}
            disabled={!canSave}
            text={sending ? 'Sending...' : 'Broadcast'}
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
            }}
          />
        </>
      }
    >
      <Box display="flex" flexDirection="column" gap={0.5} mt={0.2}>
        <DailogLabel>
          <DialogLabelText>Title:</DialogLabelText>
        </DailogLabel>
        <StyledTextField
          value={title}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setTitle(e.target.value)
          }
          disabled={sending}
          required
          fullWidth
        />

        <DailogLabel>
          <DialogLabelText>Message:</DialogLabelText>
        </DailogLabel>
        <StyledTextField
          value={body}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setBody(e.target.value)
          }
          disabled={sending}
          required
          fullWidth
          multiline
          rows={4}
        />

        <DailogLabel>
          <DialogLabelText>Expires (optional):</DialogLabelText>
        </DailogLabel>
        <DatePicker
          value={expiresAt}
          onChange={setExpiresAt}
          disabled={sending}
        />
      </Box>
    </DialogBase>
  );
};

export default BroadcastMessageDialog;
